import { useRef } from "react";

const handleScrollTo = (href: string) => {
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

/* ── Data ─────────────────────────────────────────────── */

const aboutCards = [
  {
    title: "Cadastro Técnico Arbóreo",
    description:
      "Transforme cada árvore em uma unidade técnica georreferenciada, com código CTA, fotos, FCA, vistorias e histórico completo.",
    icon: "ri-file-list-3-line",
    accent: "primary" as const,
    features: ["Código CTA único", "FCA individual", "Histórico técnico"],
  },
  {
    title: "Gestão Ambiental Urbana",
    description:
      "Monitore praças, parques, APPs urbanas, áreas verdes e Unidades de Conservação integradas ao inventário arbóreo.",
    icon: "ri-plant-line",
    accent: "accent" as const,
    features: ["Áreas verdes", "UCs e APPs", "Monitoramento contínuo"],
  },
  {
    title: "Inteligência Territorial GIS",
    description:
      "Visualize, filtre e analise o patrimônio verde em mapas com camadas de risco, espécie, bairro, projeto e indicadores ambientais.",
    icon: "ri-map-2-line",
    accent: "primary" as const,
    features: ["Mapa arbóreo", "Filtros GIS", "Indicadores territoriais"],
  },
];

const solutions = [
  {
    title: "CTA — Cadastro Técnico Arbóreo",
    description:
      "Cadastro técnico permanente para árvores, palmeiras, mudas e indivíduos arbóreos, com código único, localização, fotos e histórico.",
    icon: "ri-barcode-line",
    accent: "primary" as const,
    href: "#cta-arboreo",
  },
  {
    title: "FCA — Ficha Cadastral Arbórea",
    description:
      "Ficha individual completa com dados botânicos, fitossanitários, risco, medidas, fotos, recomendações e documentos.",
    icon: "ri-file-text-line",
    accent: "accent" as const,
    href: "#cta-arboreo",
  },
  {
    title: "Mapa Arbóreo GIS",
    description:
      "Mapa interativo com filtros por risco, espécie, bairro, projeto, status, áreas verdes e camadas ambientais.",
    icon: "ri-map-pin-line",
    accent: "primary" as const,
    href: "#ambiental",
  },
  {
    title: "Vistorias e Monitoramento",
    description:
      "Registro de vistorias técnicas, remedições, pareceres, evolução fitossanitária, risco e manejo recomendado.",
    icon: "ri-task-line",
    accent: "accent" as const,
    href: "#cta-arboreo",
  },
  {
    title: "Fotos e Evidências",
    description:
      "Organização de fotos de campo, imagens georreferenciadas, laudos, documentos técnicos e evidências vinculadas ao CTA.",
    icon: "ri-image-line",
    accent: "primary" as const,
    href: "#cta-arboreo",
  },
  {
    title: "Áreas Verdes e UCs",
    description:
      "Cadastro e monitoramento de praças, parques, APPs urbanas, áreas de recuperação e Unidades de Conservação.",
    icon: "ri-plant-line",
    accent: "accent" as const,
    href: "#ambiental",
  },
  {
    title: "Carbono e Ativos Ambientais",
    description:
      "Estimativas preliminares de biomassa, carbono e CO2e para apoiar sustentabilidade, ESG e planejamento urbano.",
    icon: "ri-cloud-line",
    accent: "primary" as const,
    href: "#carbono",
  },
  {
    title: "Relatórios Técnicos",
    description:
      "Geração de relatórios de inventário, risco, manejo, espécies, bairros, áreas verdes, UCs, fotos e carbono.",
    icon: "ri-article-line",
    accent: "accent" as const,
    href: "#carbono",
  },
];

/* ── Sub-components ───────────────────────────────────── */

function GisTextureBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
      {/* Dot grid pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(27,110,79,0.035) 1.2px, transparent 1.2px), radial-gradient(circle, rgba(0,82,204,0.025) 1px, transparent 1px)",
          backgroundSize: "36px 36px, 52px 52px",
          backgroundPosition: "0 0, 18px 26px",
        }}
      />
      {/* Subtle topographic-like curves */}
      <svg
        className="absolute bottom-0 left-0 w-full h-[55%] opacity-[0.04]"
        viewBox="0 0 1440 400"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0,320 C240,240 480,360 720,280 C960,200 1200,340 1440,260 L1440,400 L0,400 Z"
          fill="rgba(27,110,79,1)"
        />
        <path
          d="M0,360 C360,280 720,400 1080,300 C1260,250 1350,340 1440,320 L1440,400 L0,400 Z"
          fill="rgba(0,82,204,1)"
        />
      </svg>
    </div>
  );
}

function AboutCard({
  card,
}: {
  card: (typeof aboutCards)[number];
}) {
  const isPrimary = card.accent === "primary";
  return (
    <div className="relative bg-white rounded-xl p-6 md:p-8 border border-background-200/70 transition-all duration-300 hover:-translate-y-1 group">
      {/* Top accent line */}
      <div
        className={`absolute top-0 left-6 right-6 h-[2.5px] rounded-full bg-gradient-to-r from-transparent ${isPrimary ? "via-primary-400" : "via-accent-400"} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
      />

      {/* Icon bubble */}
      <div
        className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 bg-gradient-to-br ${isPrimary ? "from-primary-50 to-primary-100" : "from-accent-50 to-accent-100"} border ${isPrimary ? "border-primary-200/60" : "border-accent-200/60"} transition-all duration-300 group-hover:scale-105`}
      >
        <i
          className={`${card.icon} ${isPrimary ? "text-primary-600" : "text-accent-600"} text-2xl w-full h-full flex items-center justify-center transition-all duration-300 group-hover:brightness-110`}
        />
      </div>

      <h3 className="text-lg font-semibold text-foreground-950 mb-2.5">{card.title}</h3>
      <p className="text-sm text-foreground-600 leading-relaxed mb-4">{card.description}</p>

      {/* Mini features */}
      <ul className="space-y-1.5">
        {card.features.map((f) => (
          <li key={f} className="flex items-center gap-2">
            <div className={`w-1 h-1 rounded-full ${isPrimary ? "bg-primary-400" : "bg-accent-400"}`} />
            <span className="text-xs text-foreground-500">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SolutionCard({
  item,
}: {
  item: (typeof solutions)[number];
}) {
  const isPrimary = item.accent === "primary";
  return (
    <button
      onClick={() => handleScrollTo(item.href)}
      className={`relative text-left bg-white rounded-xl p-5 md:p-6 border border-background-200/70 transition-all duration-300 hover:-translate-y-1 group cursor-pointer ${isPrimary ? "hover:border-primary-400/70 hover:shadow-[0_8px_30px_rgba(27,110,79,0.08)]" : "hover:border-accent-400/70 hover:shadow-[0_8px_30px_rgba(0,82,204,0.08)]"}`}
    >
      {/* Icon */}
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${isPrimary ? "from-primary-50 to-primary-100" : "from-accent-50 to-accent-100"} border ${isPrimary ? "border-primary-200/60" : "border-accent-200/60"} transition-all duration-300 group-hover:scale-110`}
      >
        <i
          className={`${item.icon} ${isPrimary ? "text-primary-600" : "text-accent-600"} text-lg w-full h-full flex items-center justify-center transition-all duration-300 group-hover:brightness-110`}
        />
      </div>

      <h3 className="text-sm font-semibold text-foreground-950 mb-2">{item.title}</h3>
      <p className="text-xs text-foreground-600 leading-relaxed mb-3">{item.description}</p>

      {/* Saiba mais — appears on hover */}
      <div className="flex items-center gap-1 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
        <span className={`text-xs font-medium ${isPrimary ? "text-primary-600" : "text-accent-600"}`}>Saiba mais</span>
        <i className={`ri-arrow-right-line ${isPrimary ? "text-primary-600" : "text-accent-600"} text-xs`} />
      </div>
    </button>
  );
}

/* ── Main component ───────────────────────────────────── */

export default function AboutSolutionsSection() {
  return (
    <section id="plataforma" className="relative bg-background-50 overflow-hidden">
      <GisTextureBackground />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28">
        {/* ═══ O que é o FloraIQ ═══ */}
        <div className="text-center mb-16 md:mb-20">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary-700 bg-primary-50 px-3.5 py-1 rounded-full mb-4">
            Plataforma
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-[40px] font-bold text-foreground-950 mb-5 leading-tight">
            O que é o FloraIQ
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-3xl mx-auto leading-relaxed mb-6">
            O FloraIQ é uma plataforma SaaS para gestão inteligente do patrimônio verde urbano. Ele permite cadastrar, mapear, vistoriar e monitorar árvores, palmeiras, mudas, áreas verdes, UCs e projetos ambientais em uma base técnica, georreferenciada e permanente.
          </p>
          <div className="inline-block bg-gradient-to-r from-primary-50 via-accent-50 to-primary-50 border border-primary-200/40 rounded-xl px-6 py-4 max-w-3xl">
            <p className="text-sm md:text-base font-medium text-foreground-800 leading-relaxed">
              “Uma base técnica permanente para transformar árvores, áreas verdes e indicadores ambientais em inteligência de decisão.”
            </p>
          </div>
        </div>

        {/* About cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-24 md:mb-32">
          {aboutCards.map((card) => (
            <AboutCard key={card.title} card={card} />
          ))}
        </div>

        {/* ═══ Principais Soluções ═══ */}
        <div id="solucoes" className="scroll-mt-24">
          <div className="bg-background-100 rounded-2xl p-6 md:p-10 lg:p-14 border border-background-200/60">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-700 bg-accent-50 px-3.5 py-1 rounded-full mb-4">
                Funcionalidades
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-[40px] font-bold text-foreground-950 mb-4 leading-tight">
                Principais Soluções
              </h2>
              <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                Um conjunto completo de ferramentas para gestão ambiental urbana, inventário arbóreo e inteligência territorial.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {solutions.map((item) => (
                <SolutionCard key={item.title} item={item} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}